import { inject, Injectable, OnDestroy } from '@angular/core';
import { EnvironmentService, SessionService } from '@galaxy/core';
import { Observable, Subject } from 'rxjs';
import { VOICE_API_HOSTS } from './voice.constants';

export interface StreamingTranscriptionConfig {
  namespaceId: string;
  namespaceType: 'AccountGroup' | 'Partner';
  mimeType?: string;
  sampleRate?: number;
  channels?: number;
}

export interface TranscriptEvent {
  text: string;
  isFinal: boolean;
  isUtteranceEnd?: boolean;
}

@Injectable({ providedIn: 'root' })
export class StreamingTranscriptionService implements OnDestroy {
  private readonly environmentService = inject(EnvironmentService);
  private readonly sessionId = inject(SessionService).getSessionIdSignal();

  private sessionIdValue: string | null = null;
  private abortController: AbortController | null = null;
  private isConnected = false;

  // Session generation counter - incremented on each start()/stop() to ignore
  // stale events and protect new-session state from old-session cleanups.
  private sessionGeneration = 0;

  // Polling state
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private lastEventId = 0;
  private currentTicket: string | null = null;

  // Audio buffer for capturing audio while session is being established
  private audioBuffer: ArrayBuffer[] = [];
  private isBuffering = false;

  // Handler for browser close/navigation - attempts to stop session gracefully
  private unloadHandler: (() => void) | null = null;

  private readonly transcript$$ = new Subject<TranscriptEvent>();
  private readonly error$$ = new Subject<string>();

  readonly transcript$: Observable<TranscriptEvent> = this.transcript$$.asObservable();
  readonly error$: Observable<string> = this.error$$.asObservable();

  private get apiHost(): string {
    return VOICE_API_HOSTS.get(this.environmentService.getEnvironment())!;
  }

  private get authHeaders(): Record<string, string> {
    const token = this.sessionId();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async start(config: StreamingTranscriptionConfig): Promise<boolean> {
    // Always cleanup previous session state first
    if (this.sessionIdValue) {
      await this.stop();
    }

    // Reset all state to ensure clean start - prevents stale data from previous session
    this.sessionGeneration++;
    this.lastEventId = 0;

    if (!config.namespaceId) {
      this.error$$.next('namespaceId is required');
      return false;
    }

    if (!config.namespaceType) {
      this.error$$.next('namespaceType is required');
      return false;
    }

    const token = this.sessionId();
    if (!token) {
      this.error$$.next('No active session - cannot authenticate');
      return false;
    }

    this.abortController = new AbortController();

    // Start buffering audio immediately - audio will be sent once connected
    this.isBuffering = true;
    this.audioBuffer = [];

    try {
      const sessionId = await this.startSession(config);
      this.sessionIdValue = sessionId;

      // Register handler to stop session if browser is closed
      this.setupUnloadHandler();

      this.currentTicket = await this.getStreamTicket(sessionId);

      // Use polling instead of SSE to bypass Istio/Envoy buffering
      this.connectPolling();

      // Stop buffering and flush any audio captured while connecting
      this.isBuffering = false;
      await this.flushAudioBuffer();

      // Polling connects immediately
      return true;
    } catch (err) {
      this.cleanup(this.sessionGeneration);
      this.error$$.next(err instanceof Error ? err.message : 'Failed to start session');
      return false;
    }
  }

  private async getStreamTicket(sessionId: string): Promise<string> {
    const response = await fetch(`https://${this.apiHost}/transcribe/ticket`, {
      method: 'POST',
      headers: this.authHeaders,
      body: JSON.stringify({ session_id: sessionId }),
      signal: this.abortController?.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to get stream ticket: ${response.status}`);
    }

    const data = await response.json();
    return data.ticket;
  }

  private async startSession(config: StreamingTranscriptionConfig): Promise<string> {
    const response = await fetch(`https://${this.apiHost}/transcribe/start`, {
      method: 'POST',
      headers: this.authHeaders,
      body: JSON.stringify({
        namespace_id: config.namespaceId,
        namespace_type: config.namespaceType,
        mime_type: config.mimeType || 'audio/l16',
        sample_rate: config.sampleRate || 48000,
        channels: config.channels || 1,
      }),
      signal: this.abortController?.signal,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to start session: ${response.status} ${text}`);
    }

    const data = await response.json();
    return data.session_id;
  }

  private connectPolling(): void {
    this.isConnected = true;
    this.lastEventId = 0;

    // Capture generation at polling start to ignore events if session changes
    const pollingGeneration = this.sessionGeneration;

    this.pollingInterval = setInterval(async () => {
      if (!this.isConnected || !this.currentTicket || pollingGeneration !== this.sessionGeneration) {
        return;
      }

      try {
        // Use this.currentTicket (not closure variable) so refreshed tickets are used
        const response = await fetch(
          `https://${this.apiHost}/transcribe/events?ticket=${encodeURIComponent(this.currentTicket)}&since_id=${this.lastEventId}`,
          {
            method: 'GET',
            headers: {
              ...this.authHeaders,
              'Content-Type': 'application/json',
            },
            signal: this.abortController?.signal,
          },
        );

        if (!response.ok) {
          if (response.status === 401) {
            await this.refreshTicket();
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        // Check again after await - session might have been stopped or restarted while waiting
        if (!this.isConnected || !this.currentTicket || pollingGeneration !== this.sessionGeneration) {
          return;
        }

        if (data.events && data.events.length > 0) {
          data.events.forEach((evt: { text?: string; is_final?: boolean; is_utterance_end?: boolean; id?: number }) => {
            this.transcript$$.next({
              text: evt.text || '',
              isFinal: evt.is_final || false,
              isUtteranceEnd: evt.is_utterance_end || false,
            });
          });
          this.lastEventId = data.last_event_id;
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.error('[POLLING] Error:', err);
          // Don't change state on transient errors
        }
      }
    }, 250); // Poll every 250ms - balances latency vs backend load
  }

  private async refreshTicket(): Promise<void> {
    if (!this.sessionIdValue) {
      return;
    }

    try {
      this.currentTicket = await this.getStreamTicket(this.sessionIdValue);
    } catch {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      this.isConnected = false;
      this.error$$.next('Session expired');
    }
  }

  sendAudio(audioData: ArrayBuffer | Blob): void {
    // Capture generation to detect session changes during async operations
    const capturedGeneration = this.sessionGeneration;

    // Buffer audio while session is being established
    if (this.isBuffering) {
      if (audioData instanceof Blob) {
        audioData.arrayBuffer().then((buffer) => {
          if (capturedGeneration !== this.sessionGeneration) return;
          this.audioBuffer.push(buffer);
        });
      } else {
        this.audioBuffer.push(audioData);
      }
      return;
    }

    if (!this.sessionIdValue || !this.isConnected) {
      return;
    }

    // Send audio immediately (fire-and-forget) to avoid queue buildup
    // Deepgram handles out-of-order chunks gracefully via audio timestamps
    if (audioData instanceof Blob) {
      audioData.arrayBuffer().then((buffer) => {
        if (capturedGeneration !== this.sessionGeneration) return;
        this.sendAudioBuffer(buffer);
      });
    } else {
      this.sendAudioBuffer(audioData);
    }
  }

  private async flushAudioBuffer(): Promise<void> {
    if (this.audioBuffer.length === 0) {
      return;
    }
    const flushGeneration = this.sessionGeneration;
    const buffered = this.audioBuffer;
    this.audioBuffer = [];

    for (const buffer of buffered) {
      if (flushGeneration !== this.sessionGeneration) {
        return;
      }
      await this.sendAudioBuffer(buffer);
    }
  }

  private async sendAudioBuffer(buffer: ArrayBuffer): Promise<void> {
    if (!this.sessionIdValue) {
      return;
    }

    const base64Audio = this.arrayBufferToBase64(buffer);

    try {
      await fetch(`https://${this.apiHost}/transcribe/audio`, {
        method: 'POST',
        headers: this.authHeaders,
        body: JSON.stringify({
          session_id: this.sessionIdValue,
          audio: base64Audio,
        }),
        signal: this.abortController?.signal,
      });
    } catch {
      // Silently drop failed chunks (aborts and transient errors alike) — backend handles gaps gracefully.
    }
  }

  async stop(): Promise<void> {
    // Skip if already stopped to avoid corrupting state
    if (!this.sessionIdValue && !this.isConnected) {
      return;
    }

    // Immediately invalidate session to prevent stale events from being emitted
    this.sessionGeneration++;
    const stopGeneration = this.sessionGeneration;
    this.isConnected = false;

    if (this.sessionIdValue) {
      try {
        await fetch(`https://${this.apiHost}/transcribe/stop`, {
          method: 'POST',
          headers: this.authHeaders,
          body: JSON.stringify({
            session_id: this.sessionIdValue,
          }),
        });
      } catch (err) {
        console.error('Error stopping session:', err);
      }
    }

    this.cleanup(stopGeneration);
  }

  private cleanup(callerGeneration: number): void {
    this.removeUnloadHandler();

    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.sessionIdValue = null;
    this.currentTicket = null;
    this.lastEventId = 0;

    // If a newer start() ran while we were stopping (incrementing generation),
    // do NOT clobber its buffer/isBuffering — that state belongs to the new session.
    if (callerGeneration === this.sessionGeneration) {
      this.audioBuffer = [];
      this.isBuffering = false;
    }
  }

  private setupUnloadHandler(): void {
    this.unloadHandler = (): void => {
      if (this.sessionIdValue) {
        // Best-effort cleanup on browser close - backend inactivity timer is the safety net
        const data = JSON.stringify({ session_id: this.sessionIdValue });
        navigator.sendBeacon(`https://${this.apiHost}/transcribe/stop`, new Blob([data], { type: 'application/json' }));
      }
    };
    window.addEventListener('beforeunload', this.unloadHandler);
  }

  private removeUnloadHandler(): void {
    if (this.unloadHandler) {
      window.removeEventListener('beforeunload', this.unloadHandler);
      this.unloadHandler = null;
    }
  }

  ngOnDestroy(): void {
    if (this.sessionIdValue && navigator.sendBeacon) {
      const data = JSON.stringify({ session_id: this.sessionIdValue });
      navigator.sendBeacon(`https://${this.apiHost}/transcribe/stop`, new Blob([data], { type: 'application/json' }));
    }
    this.removeUnloadHandler();
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.transcript$$.complete();
    this.error$$.complete();
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 8192;
    const chunks: string[] = [];
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      chunks.push(String.fromCharCode.apply(null, chunk as unknown as number[]));
    }
    return btoa(chunks.join(''));
  }
}
